import { Button } from "@mui/joy";
import dayjs from "dayjs";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useRecoilState, useRecoilValue } from "recoil";
import { userAtom } from "recoil/userAtom";
import { sendMessage } from "utils/webview";
import useIsInappWebview from "hooks/useIsInappWebview";
import useGetEventFromApp from "hooks/useGetEventFromApp";
import { useCallback, useEffect, useRef, useState } from "react";
import { USER_DETAIL_ENDPOINT, USER_DOMAIN, userApi } from "api/userApi";
import ButtonWithModal from "components/common/ButtonWithModal";
import CustomModal from "components/common/CustomModal";
import { paymentOrderAtom } from "recoil/paymentOrderAtom";
import { PATH } from "routes/paths";
import { useNavigate } from "react-router-dom";
import { axiosInstance } from "../../../../api";
import { useConfirmStoreReviewerPayment } from "hooks/useConfirmStoreReviewerPayment";
import useGetDeviceInfo from "hooks/useGetDeviceInfo";
import useMixpanelWeb from "hooks/useMixpanelWeb";
import { PlanKey } from "../planTypes";
import usePaymentButtonClickTracking from "hooks/usePaymentButtonClickTracking";

export default function PaymentButtonWithPlanSelection({
  selectedPlan,
}: {
  selectedPlan: PlanKey;
}) {
  const isInappWebview = useIsInappWebview();

  const navigate = useNavigate();
  const [user, setUser] = useRecoilState(userAtom);
  const paymentOrderState = useRecoilValue(paymentOrderAtom);
  const subscription = user?.subscription;
  const parsedMessageFromApp = useGetEventFromApp();
  const queryClient = useQueryClient();

  const { appVersion } = useGetDeviceInfo();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDone, setIsDone] = useState<boolean>(false); //중복 방지
  const [disabled, setDisabled] = useState<boolean>(false);
  const [coachModal, setCoachModal] = useState<"none" | "keep" | "change">(
    "none"
  );

  const setMixPanel = useMixpanelWeb({
    eventName: "결제 버튼 클릭",
    params: {
      type: `${paymentOrderState?.paymentProvider}`,
      isFirst: `${paymentOrderState?.isFirst}`,
      event_source: "app",
    },
  });

  const { trackPaymentClick } = usePaymentButtonClickTracking({
    user,
    isInappWebview,
    appVersion,
    setMixPanel,
  });

  const { mutate: confirmStoreReviewerPayments } =
    useConfirmStoreReviewerPayment();

  // 결제 페이지(PaymentContentDefault)가 같은 키로 5초 폴링을 걸어 이 onSuccess 도 매 폴링마다 실행된다 —
  // 응답이 실제로 바뀐 때만 userAtom 에 반영해 5초마다의 전체 재렌더를 막는다 (disabled 판정은 매번 수행, 멱등).
  const lastUserDetailRef = useRef<string>();
  useQuery(
    [USER_DOMAIN, USER_DETAIL_ENDPOINT],
    useCallback(() => userApi.getDetail(), []),
    {
      enabled:
        !!axiosInstance.defaults.headers["X-Auth-Token"] && !!user?.accessToken,

      onSuccess: (data) => {
        if (data && data.result) {
          //결제 탭 닫기
          if (data.result.subscription?.isSubscribing) {
            const expiryDaysLeft = data.result.subscription?.expiryDaysLeft;
            if (expiryDaysLeft === undefined || expiryDaysLeft > 25) {
              // 넉넉잡아 25일 (원래 만료 20일 전부터 페이지 진입 가능)
              setDisabled(true);
            }
          }

          const serialized = JSON.stringify(data.result);
          if (lastUserDetailRef.current === serialized) return;
          lastUserDetailRef.current = serialized;
          setUser((user) => ({
            ...user,
            ...data.result,
          }));
        }
      },
    }
  );

  useEffect(() => {
    if (parsedMessageFromApp?.event === "payment") {
      const status = parsedMessageFromApp.params.status;
      if (status) {
        setIsLoading(false);
        if (status === "success" && !isDone) {
          queryClient.invalidateQueries([USER_DOMAIN, USER_DETAIL_ENDPOINT]);
          sendMessage(
            JSON.stringify({
              event: "navigate_tab",
              params: {
                tabName: "코칭",
              },
            })
          );
          setIsDone(true);
        }
      }
    }
  }, [isDone, parsedMessageFromApp, queryClient]);

  const buttonText = "결제하기";

  const requestPayment = (isCoachChangeRequested: boolean) => {
    if (paymentOrderState) {
      if (paymentOrderState?.isStoreReviewer) {
        setIsLoading(true);

        confirmStoreReviewerPayments(
          { orderId: paymentOrderState?.orderId!! },
          {
            onSuccess: () => {
              if (isInappWebview) {
                setIsLoading(false);

                setDisabled(true);

                sendMessage(
                  JSON.stringify({
                    event: "navigate_back",
                  })
                );
              } else {
                // 웹앱이면 메인화면으로
                navigate("/", { replace: true });
              }
            },
          }
        );

        return;
      }

      trackPaymentClick();

      const query = `plan=${selectedPlan}&is_coach_change_requested=${isCoachChangeRequested}`;

      if (isInappWebview) {
        setIsLoading(true);
        sendMessage(
          JSON.stringify({
            event: "request_toss_payments",
            params: {
              url: `${window.location.origin}${PATH.tossPayments}?${query}`,
            },
          })
        );
        setTimeout(() => {
          setIsLoading(false);
        }, 5000);
      } else {
        navigate(`${PATH.tossPayments}?${query}`);
      }
    }
  };

  // 아직 이용 기간이 남은 상태의 재결제(서버의 isExtension). 이 경우 플랜이 즉시 바뀌지 않고
  // plan_change_reservation 으로 예약만 되므로 코치도 바뀌지 않는다 — 코치 변경을 약속하는 모달을 띄우면 안 된다.
  const isExtension =
    subscription?.isSubscribing === true &&
    !!subscription?.expirationAt &&
    !dayjs(subscription.expirationAt)
      .startOf("day")
      .isBefore(dayjs().startOf("day"));

  // 재결제(isFirst=false)에서 마스터를 고르면, 플랜 업그레이드 페이지(PlanUpgradeButton)와
  // 동일하게 담당 코치 관련 안내 모달을 먼저 띄운다.
  // 퇴사(assignedCoachStatus === "inactive") 는 코치 유지 자체가 불가하므로 아래 퇴사 안내가 우선한다.
  const isMasterRepurchase =
    selectedPlan === "master" &&
    paymentOrderState?.isFirst === false &&
    !isExtension;

  const handlePrimaryClick = () => {
    if (isMasterRepurchase) {
      if (user?.assignedCoachStatus === "master_unavailable") {
        setCoachModal("change");
        return;
      }
      // 이미 마스터인 유저의 마스터 재결제는 유지가 기본 상태라 선택 모달이 불필요하다.
      if (user?.plan !== "master") {
        setCoachModal("keep");
        return;
      }
    }
    requestPayment(false);
  };

  return user?.assignedCoachStatus === "inactive" ? (
    <ButtonWithModal
      size={"lg"}
      color={"primary"}
      buttonFullWidth
      buttonText={buttonText ?? ""}
      disabled={disabled}
      loading={isLoading}
      title={"담당 코치가 변경되었어요"}
      subtitle={
        isMasterRepurchase
          ? `담당 코치는 더 이상 디스턴싱과 함께하지 않게 되었어요. 마스터 플랜은 수석 코치가 배정됩니다.`
          : `담당 코치는 더 이상 디스턴싱과 함께하지 않게 되었어요.`
      }
      textAlign="center"
      actionText="새 코치 만나기"
      action={() => {
        // 재배정은 서버가 결제 확정 시점에 처리한다 (결제 실패 시 코치가 바뀌지 않도록)
        requestPayment(false);
      }}
    />
  ) : (
    <>
      <Button
        loading={isLoading}
        disabled={disabled}
        onClick={handlePrimaryClick}
        size={"lg"}
        color={"primary"}
        sx={{ width: "100%", fontWeight: 700 }}
      >
        {buttonText}
      </Button>

      <CustomModal
        open={coachModal === "keep"}
        onClose={() => setCoachModal("none")}
        title={"담당 코치를 유지할 수 있어요"}
        subtitle={
          "마스터 플랜은 수석 코치가 배정되지만, 원하신다면 현재 담당 코치를 유지한 채 플랜만 업그레이드할 수 있습니다."
        }
        actions={[
          {
            label: "코치 유지하기",
            onClick: () => {
              setCoachModal("none");
              requestPayment(false);
            },
            variant: "Solid",
          },
          {
            label: "변경하기",
            onClick: () => {
              setCoachModal("none");
              requestPayment(true);
            },
            variant: "Plain",
          },
        ]}
      />

      <CustomModal
        open={coachModal === "change"}
        onClose={() => setCoachModal("none")}
        title={"담당 코치가 변경돼요"}
        subtitle={
          "마스터 플랜은 수석 코치가 배정되는 플랜입니다. 플랜 업그레이드 시 담당 코치가 수석 코치로 변경됩니다."
        }
        actions={[
          {
            label: "계속하기",
            onClick: () => {
              setCoachModal("none");
              requestPayment(true);
            },
            variant: "Solid",
          },
        ]}
      />
    </>
  );
}
